import { useInstanceKey, useInstanceOptions } from 'app/area/favourite/favourite-context';
import { selectLayoutAttributesForSelectedReport, selectLayoutTypeForSelectedReport, selectSelectedReport, selectWorkingDefinition,
  selectDrillDownReportForSelectedReport } from 'app/area/favourite/selectors';
import { ReportCommonApi } from 'app/area/reports/types-api';
import { DrillDownCallback, DrillDownModel } from 'app/components/favourite-layout/favourite/common/drill-down-model';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actionCreators } from './actions';
import { useActionCreators as useFavouriteActionCreators } from 'app/area/favourite/hooks';
import { reportLayoutTypeContext } from 'app/area/reports/constants';
import { getFavouriteLayoutAttributeOptions, getFavouriteLayoutAttributes } from 'app/components/favourite-layout/data-layout/selectors';
import { ChartState } from 'app/area/reports/containers/chart/types-store';
import { dashboardLayoutTypeContext } from 'app/area/dashboard/constants';

export function useActionCreators(api: ReportCommonApi) {
  const dispatch = useDispatch();
  const instanceKey = useInstanceKey();
  return useMemo(() => bindActionCreators(actionCreators(api, instanceKey), dispatch), [dispatch, instanceKey]);
}

export function useSelectedLayoutType() {
  const instanceKey = useInstanceKey();
  const layoutType = useSelector(selectLayoutTypeForSelectedReport(instanceKey));
  return codeItemToStoreValue(layoutType);
}

export function useLoadLayoutTypesEffect(api: ReportCommonApi) {
  const instanceKey = useInstanceKey();
  const selectedReport = useSelector(selectSelectedReport(instanceKey));
  const {
    layoutTypeContextId
  } = useInstanceOptions();

  if(layoutTypeContextId === dashboardLayoutTypeContext) {
    return;
  }
  const {
    loadLayoutTypes
  } = useActionCreators(api);

  useEffect(() => {
    loadLayoutTypes(selectedReport?.v);
  }, [loadLayoutTypes, selectedReport?.v]);
}

export function useDrillDownCallback(
  api: ReportCommonApi,
  favouriteDraftId?: number,
  drillDown?: DrillDownCallback
) {

  const {
    drillDownToCaseListing: drillDownDefault
  } = useActionCreators(api);

  return useCallback((favouriteId: number, model: DrillDownModel) => {
    if (drillDown) {
      drillDown(favouriteId, model);
    }
    else {
      drillDownDefault(0, favouriteDraftId, model);
    }
  }, [drillDown, drillDownDefault, favouriteDraftId]);
}

export function useAutoExecuteState() {
  const [autoExecute, setAutoExecute] = useState(true);

  const toggleAutoExecution = useCallback(() => {
    setAutoExecute(!autoExecute);
  }, [autoExecute, setAutoExecute]);

  return {
    toggleAutoExecution,
    autoExecute
  };
}

export function useFavouriteLayoutAttributes(chartState: ChartState) {
  const instanceKey = useInstanceKey();
  const layoutAttributes = useSelector(selectLayoutAttributesForSelectedReport(instanceKey));
  const {
    layoutTypeContextId
  } = useInstanceOptions();

  const { selectionReportLayout } = chartState;

  return useMemo(() => {

    const favouriteLayoutAttributes = !!selectionReportLayout && {
      ...getFavouriteLayoutAttributes(
        layoutAttributes,
        selectionReportLayout.layoutType,
        layoutTypeContextId ?? reportLayoutTypeContext
      )
    };

    const favouriteLayoutAttributeOptions = !!selectionReportLayout && { 
      ...getFavouriteLayoutAttributeOptions(
      layoutAttributes,
      selectionReportLayout.layoutType,
      layoutTypeContextId ?? reportLayoutTypeContext
    )};

    return !!selectionReportLayout && {
      layoutAttributes: {
        ...selectionReportLayout.layoutTypeAttributes,
        ...favouriteLayoutAttributes
      },
      layoutAttributeOptions: {
        ...selectionReportLayout.layoutTypeAttributes,
        ...favouriteLayoutAttributeOptions
      }
    };
  }, [layoutTypeContextId, selectionReportLayout?.layoutTypeAttributes, layoutAttributes]);
}

export function useUpdateLayoutAttributesCallback(
  selectedLayoutType: number
) {
  const {
    setWorkingDefinitionLayoutAttributes
  } = useFavouriteActionCreators();

  return useCallback((attributes: { [key: string]: string | boolean | number }[]) => {
    setWorkingDefinitionLayoutAttributes(attributes, selectedLayoutType, reportLayoutTypeContext);
  }, [selectedLayoutType]);
}

export function useExecuteFavouriteEffect(
  api: ReportCommonApi,
  autoExecute: boolean
) {
  const instanceKey = useInstanceKey();
  const workingDefinition = useSelector(selectWorkingDefinition(instanceKey));
  const {
    executionCounter,
    layoutTypeContextId
  } = useInstanceOptions();

  const {
    executeFavourite
  } = useActionCreators(api);

  useEffect(() => {
    if (autoExecute && workingDefinition) {
      executeFavourite(workingDefinition, layoutTypeContextId);
    }
  }, [
    autoExecute,
    workingDefinition,
    executionCounter,
    layoutTypeContextId
  ]);
}

export function useOnLoadOptionsDrillDownTo(api: ReportCommonApi) {
  const {
    onLoadOptionsDrillDownTo
  } = useActionCreators(api);

  return useCallback(() => {
    onLoadOptionsDrillDownTo();
  }, [onLoadOptionsDrillDownTo]);
}

export function useSelectedDrillDownReport() {
  const instanceKey = useInstanceKey();
  const drillDownReport = useSelector(selectDrillDownReportForSelectedReport(instanceKey));
  return codeItemToStoreValue(drillDownReport);
}
